import { ChevronLeft } from '@air/icons';
import { Box, IconButton, Text } from '@air/zephyr';
import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { REVALIDATE } from '~/constants/revalidate';
import { AgencyLayout } from '~/layouts/AgencyLayout';
import { getAgencyData } from '~/lib/graphcms/get-agency-data';
import { AgencyCallToActionSection } from '~/old-marketing/components/agency/AgencyCallToActionSection';
import { AgencyContainer } from '~/old-marketing/components/agency/AgencyContainer';
import { AgencyHeader } from '~/old-marketing/components/agency/AgencyHeader';
import { AgencyInformation } from '~/old-marketing/components/agency/AgencyInformation';
import { AgencyUpsell } from '~/old-marketing/components/upsells/AgencyUpsell';
import { Routes } from '~/old-marketing/constants/Routes';

export const getStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps = async ({ params }: GetStaticPropsContext) => {
  const slug = params!.slug as string;
  const { agency } = await getAgencyData({ slug });

  return {
    props: {
      agency,
      layout: {
        seo: {
          title: agency.name,
        },
      },
    },
    revalidate: REVALIDATE,
  };
};

export default function AgenciesBySlugPage({ agency }: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter();

  if (!agency || router.isFallback) return null;

  return (
    <>
      <AgencyHeader tx={{ borderBottom: '1px solid', borderBottomColor: 'pigeon100', color: 'jay600' }} />

      <AgencyContainer tx={{ my: 24 }}>
        <Link href={Routes.agencies} passHref>
          <Text
            as="a"
            tx={{
              display: 'flex',
              alignItems: 'center',
              color: 'pigeon700',
              fontWeight: 'medium',
              textDecoration: 'none',
            }}
            variant="text-ui-18"
          >
            <IconButton
              icon={ChevronLeft}
              size="medium"
              tx={{ width: 32, height: 32, mr: 4 }}
              variant="button-ghost-grey"
            >
              Go back
            </IconButton>
            Back to database
          </Text>
        </Link>
      </AgencyContainer>

      <Box tx={{ maxWidth: 774, mx: 'auto', mt: 64, px: 20, mb: 140 }}>
        <Box tx={{ textAlign: 'center' }}>
          <Text
            as="h1"
            tx={{ fontFamily: 'Brice', fontWeight: 'semibold', letterSpacing: 'unset', color: 'macaw500', mb: 16 }}
            variant="text-heading-56"
          >
            The agency directory
          </Text>
          <Text as="p" tx={{ mb: 56 }} variant="text-ui-20">
            Browse our library of agencies from across the map, offering services in marketing, design, creative
            production, content, PR, and more!
          </Text>
        </Box>

        <AgencyInformation
          clientLogos={agency.clientLogos}
          companySize={agency.companySize}
          description={agency.description.raw}
          facebookUrl={agency.facebookUrl}
          instagramUrl={agency.instagramUrl}
          linkedInUrl={agency.linkedInUrl}
          location={agency.location}
          logo={agency.logos?.[0]}
          name={agency.name}
          twitterUrl={agency.twitterUrl}
          types={agency.types}
          websiteUrl={agency.websiteUrl}
        />

        <AgencyUpsell />
      </Box>

      <AgencyCallToActionSection />
    </>
  );
}

AgenciesBySlugPage.layout = AgencyLayout;
